// src/pages/PricingPage.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import toast, { Toaster } from "react-hot-toast";

/* ------------------------------------------------------------------
   PayPal SDK loader (singleton + retry, StrictMode-safe)
   ✅ iOS Safari hard-reset support (reinject SDK if it gets "stuck")
------------------------------------------------------------------ */
const PAYPAL_SCRIPT_ID = "paypal-sdk";
let PAYPAL_LOADING_PROMISE = null;

function resetPayPalSdk() {
  try {
    const existing = document.getElementById(PAYPAL_SCRIPT_ID);
    if (existing) existing.remove();
  } catch {}
  try {
    if (window.paypal) delete window.paypal;
  } catch {}
  PAYPAL_LOADING_PROMISE = null;
}

function injectScript(clientId) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(PAYPAL_SCRIPT_ID);

    // If a script exists but with the wrong client, reset it
    if (existing && !existing.src.includes(`client-id=${encodeURIComponent(clientId)}`)) {
      existing.remove();
      if (window.paypal) delete window.paypal;
    }

    if (window.paypal) return resolve(window.paypal);

    const s = document.createElement("script");
    s.id = PAYPAL_SCRIPT_ID;

    // Allow card (debit/credit) inside the PayPal modal, block Credit/PayLater
    const qp = new URLSearchParams({
      "client-id": clientId,
      components: "buttons",
      intent: "subscription",
      vault: "true",
      "disable-funding": "credit,paylater",
    });

    s.src = `https://www.paypal.com/sdk/js?${qp.toString()}`;
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = () => reject(new Error("SDK network error"));
    document.head.appendChild(s);
  });
}

async function loadPayPalSdk(clientId, attempts = 3) {
  if (!clientId) throw new Error("Missing REACT_APP_PAYPAL_LIVE_CLIENT_ID");

  // If Safari has script but window.paypal is missing/broken, reset it.
  const hasScript = !!document.getElementById(PAYPAL_SCRIPT_ID);
  if (hasScript && !window.paypal) {
    resetPayPalSdk();
  }

  if (window.paypal) return window.paypal;

  if (!PAYPAL_LOADING_PROMISE) {
    PAYPAL_LOADING_PROMISE = (async () => {
      let lastErr;

      for (let i = 0; i < attempts; i++) {
        try {
          return await injectScript(clientId);
        } catch (err) {
          lastErr = err;
          // small backoff: 300ms, 700ms
          await new Promise((r) => setTimeout(r, 300 + i * 400));

          // ✅ if we failed, allow a clean retry next loop
          resetPayPalSdk();
        }
      }

      // ✅ allow retry next time after a total failure
      PAYPAL_LOADING_PROMISE = null;
      throw lastErr || new Error("Failed to load PayPal SDK");
    })();
  }

  try {
    return await PAYPAL_LOADING_PROMISE;
  } catch (e) {
    // ✅ if a shared promise failed, allow retry
    PAYPAL_LOADING_PROMISE = null;
    throw e;
  }
}

/* ------------------------------------------------------------------
   Single PayPal subscribe button (duplicate & double-click guard)
   - waits for enabled=true (SDK ready + trial flag loaded) before rendering
   ✅ iOS Safari: if render fails, hard-reset SDK and allow retry
------------------------------------------------------------------ */
function PayPalSubscribeButton({ planId, onApproved, enabled }) {
  const hostRef = useRef(null);
  const buttonsRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled) return; // don't render until SDK is ready + trial status loaded
    if (!hostRef.current) return;

    let cancelled = false;

    async function renderBtn() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        const paypal = await loadPayPalSdk(process.env.REACT_APP_PAYPAL_LIVE_CLIENT_ID);
        if (cancelled || !hostRef.current || !paypal) return;

        // Always clean container first (helps iOS Safari a lot)
        hostRef.current.innerHTML = "";

        const instance = paypal.Buttons({
          style: { layout: "vertical", color: "gold", shape: "rect", label: "subscribe" },

          // Block clicks while a subscription is being created
          onClick: async (_data, actions) => {
            if (inFlightRef.current) return actions.reject();
            return actions.resolve();
          },

          createSubscription: (_data, actions) =>
            (async () => {
              if (!planId) {
                toast.error("That plan is unavailable right now. Please refresh and try again.");
                return actions.reject();
              }

              if (inFlightRef.current) throw new Error("Subscription already in progress.");
              inFlightRef.current = true;

              try {
                // duplicate-subscription guard (check Supabase before creating)
                // ✅ FIX: scope the profile lookup to THIS user
                const { data: profile, error: profErr } = await supabase
                  .from("profiles")
                  .select("paypal_status")
                  .eq("id", user?.id)
                  .maybeSingle();

                if (profErr) throw new Error("Could not check your current subscription. Please try again.");
                const { data: restart, error: restartError } = await supabase
                  .from("paypal_restart_checkouts")
                  .select("id")
                  .eq("user_id", user.id)
                  .in("status", ["creating", "pending"])
                  .maybeSingle();
                if (restartError) throw new Error("Could not check for an existing checkout. Please try again.");
                if (restart) {
                  toast.error("A subscription restart is already pending. Continue from your Account page.");
                  inFlightRef.current = false;
                  return actions.reject();
                }

                if (profile?.paypal_status === "ACTIVE") {
                  toast.error("You already have an active subscription. Manage it on your Account page.");
                  inFlightRef.current = false;
                  return actions.reject();
                }

                const id = await actions.subscription.create({
                  plan_id: planId,
                  custom_id: user?.id || undefined, // webhook maps this back to Supabase
                  application_context: { user_action: "SUBSCRIBE_NOW" },
                });

                return id;
              } catch (error) {
                inFlightRef.current = false;
                throw error;
              }
            })(),

          onApprove: async (data) => {
            inFlightRef.current = false;
            toast.success("PayPal approved. Redirecting to your account…");
            onApproved?.(data);
          },

          onCancel: () => { inFlightRef.current = false; },

          onError: (err) => {
            console.error("[PayPal] onError:", err);
            inFlightRef.current = false;
            toast.error("PayPal error. Please try again.");
          },
        });

        buttonsRef.current = instance;

        // Render can fail on iOS Safari after a previous attempt; hard reset and allow retry
        await instance.render(hostRef.current);
      } catch (err) {
        console.error("[PayPal] SDK load/render failed:", err);
        inFlightRef.current = false;

        // ✅ iOS Safari hard-reset path:
        resetPayPalSdk();

        // Clear container so a second tap/route re-render can re-init
        try {
          if (hostRef.current) hostRef.current.innerHTML = "";
        } catch {}

        toast.error("PayPal didn’t load correctly. Please tap Subscribe again.");
      }
    }

    renderBtn();

    return () => {
      cancelled = true;
      try {
        buttonsRef.current?.close?.();
      } catch {}
      if (hostRef.current) hostRef.current.innerHTML = "";
      buttonsRef.current = null;
      inFlightRef.current = false;
    };
  }, [planId, onApproved, enabled]);

  if (!enabled) {
    return <div className="h-10 w-full rounded-md bg-slate-800/70 ring-1 ring-slate-700/70 animate-pulse" />;
  }
  return <div ref={hostRef} />;
}

/* ----------------------------- UI card ----------------------------- */
function PlanCard({ title, price, period = "/ month", features = [], children, highlight = false, innerRef }) {
  const isMostPopular = title.toLowerCase().includes("all access pro");

  return (
    <div
      ref={innerRef}
      className={
        "group relative flex flex-col justify-between rounded-2xl border border-slate-800/80 " +
        "bg-gradient-to-b from-slate-900/95 via-slate-950 to-slate-950/95 " +
        "p-6 md:p-7 shadow-[0_18px_45px_rgba(15,23,42,0.9)] " +
        "transition-all duration-200 hover:-translate-y-1 hover:border-blue-400/80 hover:shadow-[0_26px_70px_rgba(37,99,235,0.65)] " +
        (highlight ? "ring-2 ring-blue-400/80" : "")
      }
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 rounded-t-2xl bg-gradient-to-b from-blue-500/20 via-sky-500/10 to-transparent opacity-60" />

      {isMostPopular && (
        <div className="absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white shadow-md">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
          Most popular
        </div>
      )}

      <div className="relative">
        <h3 className="text-lg font-semibold tracking-tight text-slate-50">{title}</h3>

        <div className="mt-4 flex items-baseline gap-2">
          <div className="inline-flex items-end gap-1 rounded-full bg-slate-900/90 px-3 py-1 ring-1 ring-slate-700/80">
            <span className="text-2xl font-bold text-white">${price}</span>
            <span className="text-xs text-slate-300">{period}</span>
          </div>
        </div>

        <ul className="mt-5 space-y-2.5 text-sm leading-6 text-slate-200/90">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span
                className="
                  mt-0.5 flex h-5 w-5 flex-none items-center justify-center
                  rounded-full bg-emerald-500/90 text-[11px] font-bold text-slate-950
                  shadow-[0_0_15px_rgba(16,185,129,0.8)]
                "
              >
                ✓
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="relative mt-6">
        <div className="rounded-xl bg-slate-900/90 p-3 shadow-inner ring-1 ring-slate-800/80 group-hover:ring-blue-500/50">
          {children}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- helpers --------------------------- */
function titleToSlug(title) {
  const t = title.toLowerCase();
  if (t.includes("all access") && t.includes("pro")) return "all_access_pro";
  if (t.includes("all access") && t.includes("lite")) return "all_access_lite";
  if (t.includes("discord")) return "discord_only";

  if (t.includes("nascar") && t.includes("pro")) return "nascar_pro";
  if (t.includes("nascar") && t.includes("lite")) return "nascar_lite";

  if (t.includes("mlb") && t.includes("pro")) return "mlb_pro";
  if (t.includes("mlb") && t.includes("lite")) return "mlb_lite";

  if (t.includes("nfl") && t.includes("pro")) return "nfl_pro";
  if (t.includes("nfl") && t.includes("lite")) return "nfl_lite";

  if (t.includes("nba") && t.includes("pro")) return "nba_pro";
  if (t.includes("nba") && t.includes("lite")) return "nba_lite";

  return "";
}

/* --------------------------- Pricing page --------------------------- */
export default function PricingPage() {
  const { search } = useLocation();
  const selectedPlan = new URLSearchParams(search).get("plan") || "";
  const planRefs = useRef({});
  const [sdkReady, setSdkReady] = useState(false);

  // ✅ NEW: auth gate state
  const [authedUser, setAuthedUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // ✅ IMPORTANT: start as null so we don't accidentally render trial buttons before profile loads
  const [trialBanned, setTrialBanned] = useState(null);

  const sdkToastShown = useRef(false);

  // Trial-enabled PayPal plan IDs
  const PLAN_IDS_TRIAL = {
    "Discord Access": "P-96N94697095892935NC23XXI",

    "NBA LITE Member": "P-8T5580076P393200FNC23WBI",
    "MLB LITE Member": "P-0DK27985LU908842VNC23VJQ",
    "NFL LITE Member": "P-9A079862R35074028NC23UOA",
    "NASCAR LITE Member": "P-5CH30718EJ5817631NC23TSI",

    "NBA PRO Member": "P-55W83452GH8917325NC23SVQ",
    "MLB PRO Member": "P-83Y13089DD870461TNC23R3I",
    "NFL PRO Member": "P-7EV8463063412251LNC23Q6Y",
    "NASCAR PRO Member": "P-8G568744214719119NC23QEA",

    "All Access LITE": "P-01112034F4978121RNC23PBY",
    "All Access PRO": "P-3NA07489RA706953DNC23NOI",
  };

  // No-trial PayPal plan IDs
  const PLAN_IDS_NO_TRIAL = {
    "All Access PRO": "P-84T83038RN1606140NFQKAJQ",
    "All Access LITE": "P-5XN628470C358824KNFQP3UQ",

    "NASCAR PRO Member": "P-2RU64464F86933844NFQP5PY",
    "NFL PRO Member": "P-7BU88906YP511011TNFQP62Y",
    "MLB PRO Member": "P-3XD0259501416114LNFQP74Q",
    "NBA PRO Member": "P-5DR70159TB841871FNFQQBMA",

    "NASCAR LITE Member": "P-3B486317S5788142VNFQQCXI",
    "NFL LITE Member": "P-66E8723514489942TNFQQDXY",
    "MLB LITE Member": "P-9WP73914KW4046948NFQQFJI",
    "NBA LITE Member": "P-7GG55104XJ955432UNFQQGJI",

    "Discord Access": "P-41C688164B1374449NFQQHLI",
  };

  const ACTIVE_PLAN_IDS = trialBanned === true ? PLAN_IDS_NO_TRIAL : PLAN_IDS_TRIAL;

  // ✅ NEW: load auth state once (for gating PayPal)
  useEffect(() => {
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      setAuthedUser(user || null);
      setAuthChecked(true);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setTrialBanned(false);
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("trial_banned")
        .eq("id", user.id)
        .maybeSingle();

      if (error) {
        console.warn("[PricingPage] trial_banned lookup error:", error);
        setTrialBanned(false);
        return;
      }

      setTrialBanned(!!data?.trial_banned);
    })();
  }, []);

  const handleApproved = () => {
    window.location.href = "/account";
  };

  // Preload PayPal SDK
  useEffect(() => {
    (async () => {
      try {
        await loadPayPalSdk(process.env.REACT_APP_PAYPAL_LIVE_CLIENT_ID);
        setSdkReady(true);
      } catch (err) {
        console.error("[PayPal] SDK preload failed:", err);
        // ✅ allow retry next render/tap
        resetPayPalSdk();

        if (!sdkToastShown.current) {
          sdkToastShown.current = true;
          toast.error("Failed to load PayPal. Please refresh and try again.");
        }
        setSdkReady(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedPlan) return;
    const el = planRefs.current[selectedPlan];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-blue-500", "shadow-xl");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-blue-500", "shadow-xl");
      }, 1800);
    }
  }, [selectedPlan]);

const litePlans = [
  {
    title: "MLB LITE Member",
    price: 30,
    features: [
      "Player projections & ownership",
      "Cheat sheets & data tables",
      "Range of Outcomes tools",
      "Top stacks & slate rankings",
      "Floor / ceiling projections",
      "Discord access",
      "Optimizer & Late Swap available in PRO",
    ],
  },
  {
    title: "NASCAR LITE Member",
    price: 30,
    features: [
      "Driver projections & ownership",
      "Cheat sheets & data tables",
      "Range of Outcomes tools",
      "Driver simulations",
      "Track, practice & qualifying data",
      "Xfinity & Trucks included",
      "Discord access",
      "Optimizer & advanced sim tools available in PRO",
    ],
  },
  {
    title: "NFL LITE Member",
    price: 30,
    features: [
      "Player projections & ownership",
      "Cheat sheets & data tables",
      "Range of Outcomes tools",
      "Projected player stats",
      "Stacking tools & optimal rates",
      "Discord access",
      "Optimizer & Late Swap available in PRO",
    ],
  },
  {
    title: "NBA LITE Member",
    price: 30,
    features: [
      "Player projections & ownership",
      "Cheat sheets & data tables",
      "Range of Outcomes tools",
      "Value & slate data",
      "Floor / ceiling projections",
      "Discord access",
      "Optimizer & Late Swap available in PRO",
    ],
  },
];

const proPlans = [
  {
    title: "MLB PRO Member",
    price: 40,
    features: [
      "Everything in MLB LITE",
      "Lineup Optimizer",
      "Late Swap tools",
    ],
  },
  {
    title: "NASCAR PRO Member",
    price: 40,
    features: [
      "Everything in NASCAR LITE",
      "Lineup Optimizer",
      "Contest Simulator",
      "Portfolio Builder",
      "Post-sim lineup analysis",
    ],
  },
  {
    title: "NFL PRO Member",
    price: 40,
    features: [
      "Everything in NFL LITE",
      "Lineup Optimizer",
      "Late Swap tools",
      "Classic & showdown tools",
    ],
  },
  {
    title: "NBA PRO Member",
    price: 40,
    features: [
      "Everything in NBA LITE",
      "Lineup Optimizer",
      "Late Swap tools",
    ],
  },
];

const allAccess = {
  title: "All Access PRO",
  price: 50,
  features: [
    "All sports included",
    "All LITE + PRO tools",
    "All projections, ownership & data tools",
    "All Range of Outcomes tools",
    "All Optimizers included",
    "Late Swap included for NFL, MLB & NBA",
    "NASCAR Contest Simulator & Portfolio Builder included",
    "All Discord channels",
  ],
};

const allAccessLite = {
  title: "All Access LITE",
  price: 40,
  features: [
    "All LITE tiers across every sport",
    "All projections, ownership & data tools",
    "All Range of Outcomes tools",
    "All cheat sheets & slate data",
    "All Discord channels",
    "Does not include Optimizers or Late Swap",
  ],
};

const discordOnly = {
  title: "Discord Access",
  price: 10,
  features: [
    "Private Discord channels",
    "Slate notes & strategy talk",
    "DFS community support",
    "Lineup discussion & questions",
    "Does not include site tools or projections",
  ],
};

  // ✅ Only enable PayPal buttons once:
  // - SDK is ready
  // - trialBanned has been resolved (true/false)
  const buttonsEnabled = sdkReady && trialBanned !== null;

  const getPlanId = (title) => {
    if (trialBanned === null) return null;
    const id = ACTIVE_PLAN_IDS[title];
    if (!id) console.warn("[PricingPage] missing planId for:", title, { trialBanned });
    return id;
  };

  // ✅ NEW: shared "login to subscribe" button
  const LoginToSubscribeButton = () => (
    <button
      onClick={() => (window.location.href = "/login")}
      className="w-full rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500 transition"
    >
      Log in to subscribe
    </button>
  );

  // ✅ NEW: prevent any PayPal buttons from showing until we know auth state
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#050816] via-[#050F26] to-[#020617] text-slate-100">
        <Toaster position="top-center" reverseOrder={false} />
        <div className="flex min-h-screen items-center justify-center text-slate-300">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050816] via-[#050F26] to-[#020617] text-slate-100">
      <Toaster position="top-center" reverseOrder={false} />

      <section className="relative overflow-hidden border-b border-slate-800/60 bg-gradient-to-br from-[#050816] via-[#050F26] to-[#020617]">
        <div className="pointer-events-none absolute inset-0 opacity-50">
          <div className="absolute -top-40 left-10 h-72 w-72 rounded-full bg-blue-500/25 blur-3xl" />
          <div className="absolute bottom-[-140px] right-[-60px] h-80 w-80 rounded-full bg-blue-700/25 blur-3xl" />
        </div>

        <div className="relative mx-auto flex max-w-7xl flex-col gap-6 px-4 py-12 sm:px-6 sm:py-16 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-18">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/40 bg-slate-950/80 px-3 py-1 text-xs font-medium text-blue-100 shadow-[0_0_25px_rgba(37,99,235,0.5)]">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Cpenn DFS Memberships
            </div>

            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Choose your edge for{" "}
              <span className="bg-gradient-to-r from-sky-300 via-blue-400 to-blue-500 bg-clip-text text-transparent">
                every slate
              </span>
              .
            </h1>

            <p className="mt-4 max-w-xl text-sm sm:text-base text-slate-200/90">
              Pick the sport or bundle that fits how you play. Projections, ownership, data, optimizers, and Discord –
              all in one place.
            </p>

            {trialBanned === false && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-2 text-xs sm:text-sm text-emerald-50">
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
                You’re eligible for a 7 day free trial — subscribe below to activate it.
              </div>
            )}
          </div>

          <div className="mt-6 w-full max-w-sm shrink-0 rounded-2xl border border-slate-800/80 bg-slate-950/80 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.9)] sm:mt-8">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Quick overview</p>
            <ul className="mt-4 space-y-3 text-sm text-slate-100">
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-500/90 text-[11px] font-bold">
                  1
                </span>
                <span>
                  <span className="font-semibold">Lite</span> – projections, ownership, data & Discord per sport.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-500/90 text-[11px] font-bold">
                  2
                </span>
                <span>
                  <span className="font-semibold">Pro</span> – everything in Lite plus Optimizers.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-500/90 text-[11px] font-bold">
                  3
                </span>
                <span>
                  <span className="font-semibold">All Access</span> – all sports, all tools, one membership.
                </span>
              </li>
            </ul>
            <p className="mt-4 text-[11px] text-slate-400">
              Pay safely via PayPal. Subscriptions can be managed anytime from your Account page.
            </p>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14">
        <section>
          <div className="text-center">
            <h2 className="text-xl font-semibold tracking-tight text-slate-100 sm:text-2xl">
              Lite plans – tools for each sport
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              Sport-specific access without Optimizers. Perfect if you’re locked in on one sport.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {litePlans.map((p, i) => {
              const slug = titleToSlug(p.title);
              return (
                <PlanCard
                  key={`lite-${i}`}
                  {...p}
                  highlight={selectedPlan === slug}
                  innerRef={(el) => {
                    if (el) planRefs.current[slug] = el;
                  }}
                >
                  {authedUser ? (
                    <PayPalSubscribeButton
                      planId={getPlanId(p.title)}
                      onApproved={handleApproved}
                      enabled={buttonsEnabled}
                    />
                  ) : (
                    <LoginToSubscribeButton />
                  )}
                </PlanCard>
              );
            })}
          </div>
        </section>

        <section className="mt-12 border-t border-slate-800/80 pt-10">
          <div className="text-center">
            <h2 className="text-xl font-semibold tracking-tight text-slate-100 sm:text-2xl">
              Pro plans – add Optimizers
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              Everything in Lite, plus lineup Optimizers and deeper tools for each sport.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {proPlans.map((p, i) => {
              const slug = titleToSlug(p.title);
              return (
                <PlanCard
                  key={`pro-${i}`}
                  {...p}
                  highlight={selectedPlan === slug}
                  innerRef={(el) => {
                    if (el) planRefs.current[slug] = el;
                  }}
                >
                  {authedUser ? (
                    <PayPalSubscribeButton
                      planId={getPlanId(p.title)}
                      onApproved={handleApproved}
                      enabled={buttonsEnabled}
                    />
                  ) : (
                    <LoginToSubscribeButton />
                  )}
                </PlanCard>
              );
            })}
          </div>
        </section>

        <section className="mt-12 border-t border-slate-800/80 pt-10">
          <div className="text-center">
            <h2 className="text-xl font-semibold tracking-tight text-slate-100 sm:text-2xl">All Access & Discord</h2>
            <p className="mt-2 text-sm text-slate-400">
              Get everything in one shot or hop into Discord only – whatever fits your setup.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            <PlanCard
              {...allAccess}
              highlight={selectedPlan === "all_access_pro"}
              innerRef={(el) => {
                if (el) planRefs.current["all_access_pro"] = el;
              }}
            >
              {authedUser ? (
                <PayPalSubscribeButton
                  planId={getPlanId("All Access PRO")}
                  onApproved={handleApproved}
                  enabled={buttonsEnabled}
                />
              ) : (
                <LoginToSubscribeButton />
              )}
            </PlanCard>

            <PlanCard
              {...allAccessLite}
              highlight={selectedPlan === "all_access_lite"}
              innerRef={(el) => {
                if (el) planRefs.current["all_access_lite"] = el;
              }}
            >
              {authedUser ? (
                <PayPalSubscribeButton
                  planId={getPlanId("All Access LITE")}
                  onApproved={handleApproved}
                  enabled={buttonsEnabled}
                />
              ) : (
                <LoginToSubscribeButton />
              )}
            </PlanCard>

            <PlanCard
              {...discordOnly}
              highlight={selectedPlan === "discord_only"}
              innerRef={(el) => {
                if (el) planRefs.current["discord_only"] = el;
              }}
            >
              {authedUser ? (
                <PayPalSubscribeButton
                  planId={getPlanId("Discord Access")}
                  onApproved={handleApproved}
                  enabled={buttonsEnabled}
                />
              ) : (
                <LoginToSubscribeButton />
              )}
            </PlanCard>
          </div>
        </section>
      </main>
    </div>
  );
}
